import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getHistoricoFornecedor } from "../../services/monitoramentoSerasa";
import type { HistoricoFornecedor } from "../../models/monitoramentoSerasa/types";
import {
  serieCompactaParaLinhas,
  serieMensalParaLinhas,
} from "../../models/monitoramentoSerasa/types";
import { cnpjTem14Digitos, formatCnpjLeitura, somenteDigitosCnpj } from "./cnpj";
import {
  ApiErrorPanel,
  formatBRL,
  formatDataHora,
  KpiCard,
  PainelRodape,
  SegmentoChip,
  corBordaSeveridadeMui,
  severidadeComoTexto,
} from "./shared";

function deltaBRL(atual?: number, anterior?: number): string | null {
  if (atual == null || anterior == null) return null;
  const d = atual - anterior;
  const s = d >= 0 ? "+" : "";
  return `${s}${formatBRL(d)}`;
}

/** Rótulo curto no eixo X (data da consulta). */
function eixoXDataConsulta(iso: string): string {
  if (!iso) return "";
  try {
    const d = parseISO(iso.includes("T") ? iso : iso.replace(" ", "T"));
    return isValid(d) ? format(d, "dd/MM/yy", { locale: ptBR }) : iso.slice(0, 10);
  } catch {
    return iso.slice(0, 10);
  }
}

function eixoYMoedaCompacta(v: number): string {
  if (v == null || Number.isNaN(v)) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(v);
}

export const MonitoramentoFornecedor = () => {
  const { cnpj } = useParams<{ cnpj: string }>();
  const navigate = useNavigate();
  const cnpj14 = somenteDigitosCnpj(cnpj);
  const valido = cnpjTem14Digitos(cnpj14);

  const [data, setData] = useState<HistoricoFornecedor | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [pageMarcos, setPageMarcos] = useState(0);
  const [rowsMarcos, setRowsMarcos] = useState(10);

  const carregar = useCallback(async () => {
    if (!valido) return;
    setLoading(true);
    setErro("");
    try {
      const h = await getHistoricoFornecedor(cnpj14, {
        incluirMarcos: true,
        incluirSerie: true,
        incluirSerieMensal: true,
      });
      setData(h);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Não foi possível carregar o histórico.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, [cnpj14, valido]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    setPageMarcos(0);
  }, [data?.marcos]);

  const marcosOrdenados = useMemo(() => {
    const list = [...(data?.marcos ?? [])];
    list.sort((a, b) => {
      const ta = new Date(a.dataConsulta ?? 0).getTime();
      const tb = new Date(b.dataConsulta ?? 0).getTime();
      return tb - ta;
    });
    return list;
  }, [data?.marcos]);

  const marcosPagina = useMemo(() => {
    const ini = pageMarcos * rowsMarcos;
    return marcosOrdenados.slice(ini, ini + rowsMarcos);
  }, [marcosOrdenados, pageMarcos, rowsMarcos]);

  /** Pontos em ordem cronológica crescente (API pode enviar em qualquer ordem). */
  const pontosOrdenadosAsc = useMemo(() => {
    const list = [...(data?.pontos ?? [])];
    list.sort(
      (a, b) =>
        new Date(a.dataConsulta ?? 0).getTime() - new Date(b.dataConsulta ?? 0).getTime()
    );
    return list;
  }, [data?.pontos]);

  const ultimo = pontosOrdenadosAsc[pontosOrdenadosAsc.length - 1];
  const anterior =
    pontosOrdenadosAsc.length >= 2
      ? pontosOrdenadosAsc[pontosOrdenadosAsc.length - 2]
      : undefined;

  /** Primeira data: menor entre `primeiraConsultaUtc` e o snapshot mais antigo retornado. */
  const dataPrimeiraCabecalho = useMemo(() => {
    const api = data?.primeiraConsultaUtc;
    const doHistorico = pontosOrdenadosAsc[0]?.dataConsulta;
    if (!api && !doHistorico) return undefined;
    if (!api) return doHistorico;
    if (!doHistorico) return api;
    return new Date(api).getTime() <= new Date(doHistorico).getTime() ? api : doHistorico;
  }, [data?.primeiraConsultaUtc, pontosOrdenadosAsc]);

  const dataUltimaCabecalho = ultimo?.dataConsulta;

  const legendaPeriodoDelta =
    anterior?.dataConsulta && dataUltimaCabecalho
      ? `${formatDataHora(anterior.dataConsulta)} → ${formatDataHora(dataUltimaCabecalho)}`
      : dataUltimaCabecalho
      ? `Consulta única: ${formatDataHora(dataUltimaCabecalho)}`
      : null;

  /** Rótulo curto “vs data anterior” (mantido para trechos JSX que ainda referenciam `refPrev`). */
  const refPrev = anterior?.dataConsulta
    ? ` vs ${formatDataHora(anterior.dataConsulta)}`
    : "";

  const serie = useMemo(
    () => serieCompactaParaLinhas(data?.serieCompacta ?? null),
    [data]
  );

  const serieMensalLinhas = useMemo(
    () => serieMensalParaLinhas(data?.serieMensal ?? null),
    [data]
  );

  const temSerieQtd = useMemo(
    () => serie.some((p) => p.quantidadeTotalAnotacoes !== undefined && p.quantidadeTotalAnotacoes !== null),
    [serie]
  );

  if (!valido) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          CNPJ inválido na URL. Use 14 dígitos sem máscara.
        </Alert>
        <Button component={RouterLink} to="/monitoramento/carteira">
          Voltar para carteira
        </Button>
      </Box>
    );
  }

  const deltaAnot = ultimo?.deltaEmRelacaoAnterior?.valorTotalAnotacoes;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Voltar
      </Button>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>
          {ultimo?.razaoSocial || "Fornecedor"}
        </Typography>
        <Typography color="text.secondary" variant="subtitle1">
          {formatCnpjLeitura(cnpj14)}
        </Typography>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <SegmentoChip segmento={ultimo?.segmentoCarteira} />
          </Box>
          {dataPrimeiraCabecalho ? (
            <Typography variant="body2" color="text.secondary">
              <strong>Primeira consulta:</strong> {formatDataHora(dataPrimeiraCabecalho)}
            </Typography>
          ) : null}
          {dataUltimaCabecalho ? (
            <Typography variant="body2" color="text.secondary">
              <strong>Última consulta:</strong> {formatDataHora(dataUltimaCabecalho)}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            Valores dos cards são da <strong>última</strong> consulta; Δ compara última com a{" "}
            <strong>penúltima</strong> quando existir.
          </Typography>
        </Box>
      </Box>

      {erro ? (
        <Box sx={{ mb: 2 }}>
          <ApiErrorPanel message={erro} onRetry={carregar} />
        </Box>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Valor total anotações"
            loading={loading}
            valor={
              <>
                {formatBRL(ultimo?.valorTotalAnotacoes)}
                {deltaAnot != null ? (
                  <>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Δ consolidado (API): {formatBRL(deltaAnot)}
                    </Typography>
                    {legendaPeriodoDelta ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {legendaPeriodoDelta}
                      </Typography>
                    ) : null}
                  </>
                ) : deltaBRL(ultimo?.valorTotalAnotacoes, anterior?.valorTotalAnotacoes) ? (
                  <>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {deltaBRL(ultimo?.valorTotalAnotacoes, anterior?.valorTotalAnotacoes)}
                    </Typography>
                    {legendaPeriodoDelta ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {legendaPeriodoDelta}
                      </Typography>
                    ) : null}
                  </>
                ) : null}
              </>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Protestos (qtd / valor)"
            loading={loading}
            valor={
              <>
                {ultimo?.quantidadeProtestos ?? "—"} /{" "}
                {formatBRL(ultimo?.valorProtestos)}
                <Typography variant="caption" display="block" color="text.secondary">
                  {deltaBRL(ultimo?.valorProtestos, anterior?.valorProtestos) || "—"}
                </Typography>
                {legendaPeriodoDelta ? (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {legendaPeriodoDelta}
                  </Typography>
                ) : null}
              </>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Ações judiciais"
            loading={loading}
            valor={
              <>
                {ultimo?.quantidadeAcoesJudiciais ?? "—"} /{" "}
                {formatBRL(ultimo?.valorAcoesJudiciais)}
                <Typography variant="caption" display="block" color="text.secondary">
                  {deltaBRL(ultimo?.valorAcoesJudiciais, anterior?.valorAcoesJudiciais) || "—"}
                </Typography>
                {legendaPeriodoDelta ? (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {legendaPeriodoDelta}
                  </Typography>
                ) : null}
              </>
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Dívidas vencidas"
            loading={loading}
            valor={
              <>
                {ultimo?.quantidadeDividasVencidas ?? "—"} /{" "}
                {formatBRL(ultimo?.valorDividasVencidas)}
                <Typography variant="caption" display="block" color="text.secondary">
                  {deltaBRL(ultimo?.valorDividasVencidas, anterior?.valorDividasVencidas) || "—"}
                </Typography>
                {legendaPeriodoDelta ? (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {legendaPeriodoDelta}
                  </Typography>
                ) : null}
              </>
            }
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Evolução — valor total de anotações
          </Typography>
          <Box sx={{ width: "100%", height: 320 }}>
            {loading ? (
              <Typography color="text.secondary">Carregando…</Typography>
            ) : serie.length < 2 ? (
              <Typography color="text.secondary">
                {serie.length === 0
                  ? "Sem série retornada (serieCompacta)."
                  : "Monitoramento com uma consulta até o momento — série completa após novas cargas."}
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={serie}
                  margin={{
                    top: 12,
                    right: temSerieQtd ? 56 : 20,
                    left: 20,
                    bottom: 52,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default)" />
                  <XAxis
                    dataKey="data"
                    type="category"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => eixoXDataConsulta(String(v))}
                    angle={-30}
                    textAnchor="end"
                    height={56}
                    interval="preserveStartEnd"
                    label={{
                      value: "Data da consulta",
                      position: "insideBottom",
                      offset: -8,
                      style: { fontSize: 12, fill: "var(--text-secondary)" },
                    }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => eixoYMoedaCompacta(Number(v))}
                    width={72}
                    label={{
                      value: "Valor total de anotações (R$)",
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle", fontSize: 12, fill: "var(--info)" },
                      offset: 10,
                    }}
                  />
                  {temSerieQtd ? (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                      width={44}
                      label={{
                        value: "Qtd. total anotações",
                        angle: 90,
                        position: "insideRight",
                        style: { textAnchor: "middle", fontSize: 12, fill: "var(--dv-1)" },
                        dx: 6,
                      }}
                    />
                  ) : null}
                  <Tooltip
                    labelFormatter={(label) =>
                      label ? `Consulta: ${formatDataHora(String(label))}` : ""
                    }
                  formatter={(v: any, name: any) => {
                    const isQuantity = name === "quantidadeTotalAnotacoes" || name === "Qtd. anotações";
                    
                    return isQuantity
                      ? [v ?? 0, "Qtd. total anotações"]
                      : [formatBRL(Number(v ?? 0)), "Valor total anotações"];
                  }}
                  />
                  <Legend verticalAlign="top" height={28} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="valorTotalAnotacoes"
                    name="Valor total (R$)"
                    stroke="var(--info)"
                    strokeWidth={2.5}
                    dot={{ r: 3, strokeWidth: 1, fill: "var(--text-on-inverse)" }}
                    activeDot={{ r: 5 }}
                  />
                  {temSerieQtd ? (
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="quantidadeTotalAnotacoes"
                      name="Qtd. total anotações"
                      stroke="var(--dv-1)"
                      strokeWidth={2}
                      dot={{ r: 3, strokeWidth: 1, fill: "var(--text-on-inverse)" }}
                    />
                  ) : null}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {serieMensalLinhas.length > 0 ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Série mensal (última consulta do mês)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Cada mês usa só a <strong>última consulta</strong> daquele mês (totais = retrato dessa
              consulta). <code>marcosDoMes</code>: eventos apenas da transição consulta anterior →
              última do mês.
            </Typography>
            <Box sx={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={serieMensalLinhas} margin={{ bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="anoMes" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const row = payload[0].payload as (typeof serieMensalLinhas)[0];
                      return (
                        <Box
                          sx={{
                            bgcolor: "background.paper",
                            p: 1,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" display="block">
                            {row.anoMes}
                          </Typography>
                          <Typography variant="body2">
                            Valor anotações: {formatBRL(row.valorTotalAnotacoes)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Qtd. anotações: {row.quantidadeTotalAnotacoes ?? "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Última consulta (mês):{" "}
                            {row.ultimaConsultaNoMesUtc
                              ? formatDataHora(row.ultimaConsultaNoMesUtc)
                              : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Consultas no mês: {row.consultasNoMes ?? "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Protestos: {row.quantidadeProtestos ?? "—"} /{" "}
                            {formatBRL(row.valorProtestos)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Ações judiciais: {row.quantidadeAcoesJudiciais ?? "—"} /{" "}
                            {formatBRL(row.valorAcoesJudiciais)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Marcos no mês: {row.marcosNoMes}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                  <Legend />
                  <Bar dataKey="valorTotalAnotacoes" name="Valor anotações" fill="var(--info)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {(data?.insights?.length ?? 0) > 0 ? (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Insights
            </Typography>
            {(data?.insights ?? []).map((t, i) => (
              <Typography key={i} variant="body2" color="text.secondary" display="block">
                • {t}
              </Typography>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Marcos e alertas
          </Typography>
          {marcosOrdenados.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ pb: 2 }}>
              Nenhum marco retornado em <code>marcos</code>.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Ordenados da data mais recente para a mais antiga. Total: {marcosOrdenados.length}.
              </Typography>
              <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={100}>Data</TableCell>
                      <TableCell>Severidade</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right" width={160}>
                        Δ valor anotações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {marcosPagina.map((m, i) => {
                      const antes = m.valoresAntes?.valorTotalAnotacoes;
                      const depois = m.valoresDepois?.valorTotalAnotacoes;
                      const delta =
                        antes != null && depois != null ? depois - antes : null;
                      const sev = m.severidade;
                      const chipColor =
                        sev === 2 ? "error" : sev === 1 ? "warning" : sev === 0 ? "info" : "default";
                      return (
                        <TableRow
                          key={`${m.dataConsulta}-${m.tipoEvento}-${pageMarcos * rowsMarcos + i}`}
                          sx={{
                            "& td:first-of-type": {
                              borderLeft: 4,
                              borderColor: corBordaSeveridadeMui(sev),
                            },
                          }}
                        >
                          <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                            {formatDataHora(m.dataConsulta)}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            <Chip
                              size="small"
                              label={severidadeComoTexto(sev) || "—"}
                              color={chipColor}
                              variant={chipColor === "default" ? "outlined" : "filled"}
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                            {m.tipoEvento || "—"}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", maxWidth: 360 }}>
                            <Typography variant="body2">{m.mensagemCurta || "—"}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                            {delta != null ? (
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color={delta > 0 ? "error.main" : delta < 0 ? "success.main" : "text.secondary"}
                              >
                                {delta > 0 ? "+" : ""}
                                {formatBRL(delta)}
                              </Typography>
                            ) : (
                              "—"
                            )}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {antes != null ? formatBRL(antes) : "—"} → {depois != null ? formatBRL(depois) : "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={marcosOrdenados.length}
                rowsPerPage={rowsMarcos}
                page={pageMarcos}
                onPageChange={(_, p) => setPageMarcos(p)}
                onRowsPerPageChange={(e) => {
                  setRowsMarcos(parseInt(e.target.value, 10));
                  setPageMarcos(0);
                }}
                labelRowsPerPage="Itens por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      <PainelRodape versaoRegras={data?.versaoRegras} />
    </Box>
  );
};
